import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ScanLine,
  Printer,
  Map,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  History,
  Users as UsersIcon,
  PackageCheck,
  Briefcase,
  Truck,
  ClipboardCheck,
  Send,
  Tag,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, tid: "nav-dashboard", module: null },
  { to: "/artikel", icon: Package, label: "Artikel", tid: "nav-articles", module: "articles" },
  { to: "/gruppen", icon: Tag, label: "Gruppen", tid: "nav-groups", module: "articles" },
  { to: "/scanner", icon: ScanLine, label: "Scanner", tid: "nav-scanner", module: "articles" },
  { to: "/abhollager", icon: PackageCheck, label: "Abhollager", tid: "nav-pickup", module: "pickup" },
  { to: "/projekte", icon: Briefcase, label: "Projekte", tid: "nav-projects", module: "projects" },
  { to: "/historie", icon: History, label: "Historie", tid: "nav-history", module: "history" },
  { to: "/etiketten", icon: Printer, label: "Etiketten", tid: "nav-labels", module: "labels" },
  { to: "/lagerplan", icon: Map, label: "Lagerplan", tid: "nav-floorplan", module: "shelves" },
  { to: "/lieferanten", icon: Truck, label: "Lieferanten", tid: "nav-suppliers", module: "suppliers" },
  { to: "/bestellungen", icon: Send, label: "Bestellungen", tid: "nav-orders", module: "suppliers" },
  { to: "/inventur", icon: ClipboardCheck, label: "Inventur", tid: "nav-inventory", module: "articles" },
  { to: "/benutzer", icon: UsersIcon, label: "Benutzer", tid: "nav-users", adminOnly: true },
  { to: "/einstellungen", icon: SettingsIcon, label: "Einstellungen", tid: "nav-settings", adminOnly: true },
];

// Returns true if the user can see this nav entry
function canSee(item, user) {
  if (item.adminOnly) return user?.role === "admin";
  if (user?.role === "admin") return true;
  if (!item.module) return true;
  const lvl = user?.permissions?.[item.module] || "none";
  return lvl === "read" || lvl === "write";
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = (
    <aside className="w-64 bg-black text-white flex flex-col border-r border-zinc-800 h-full">
      <div className="px-5 py-5 border-b border-zinc-800 flex items-center gap-3">
        <div className="w-12 h-12 bg-white p-1 flex items-center justify-center shrink-0">
          <img
            src="/logo-celentano.png"
            alt="Celentano"
            className="w-full h-full object-contain"
          />
        </div>
        <div className="min-w-0">
          <div className="font-display text-base leading-none uppercase truncate">Celentano</div>
          <div className="text-[10px] tracking-[0.18em] text-zinc-400 mt-1 mono">
            LAGERVERWALTUNG
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto no-scrollbar">
        {NAV.filter((n) => canSee(n, user)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.tid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                isActive
                  ? "bg-zinc-900 border-[#F97316] text-white"
                  : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900"
              }`
            }
          >
            <n.icon className="w-4 h-4" strokeWidth={2} />
            <span className="tracking-wide">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-zinc-800 p-3">
        <div className="px-2 py-2 mb-2">
          <div className="text-[10px] tracking-[0.2em] text-zinc-500 mono">ANGEMELDET</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-email">
            {user?.email}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full justify-start bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-white rounded-sm"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Abmelden
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-[#F4F4F5]">
      {/* Desktop sidebar */}
      <div className="hidden lg:block lg:sticky lg:top-0 lg:h-screen">{Sidebar}</div>

      {/* Mobile sidebar overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative h-full">{Sidebar}</div>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="lg:hidden flex items-center justify-between bg-black text-white px-4 py-3 border-b border-zinc-800 sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="p-1.5 hover:bg-zinc-900"
              aria-label="Menü öffnen"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <img src="/logo-celentano.png" alt="" className="w-7 h-7 object-contain bg-white p-0.5" />
            <span className="font-display uppercase text-sm">Celentano Lager</span>
          </div>
          <span className="mono text-[9px] tracking-[0.18em] text-zinc-400">v1.2</span>
        </header>

        <div className="flex-1 p-3 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
